/**
 * Transcript Refinement Service
 *
 * Frontend service for interacting with the transcript revision and refinement APIs.
 */

const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? '/api'
  : 'http://localhost:7777/api';

export interface TranscriptRevision {
  checksum: string;
  version: number;
  parent_checksum: string | null;
  root_checksum: string;
  created_at: string;
  created_by: string | null;
  model: string | null;
  cost_usd: number | null;
  revision_notes: string | null;
}

export interface TranscriptDiffgram {
  version: number;
  baseChecksum: string;
  edits: Array<{
    id: string;
    type: 'replace' | 'insert' | 'delete';
    timeRange: { startTime: number; endTime: number };
    original: { text: string };
    replacement?: { text: string };
    timingStrategy: 'preserve' | 'redistribute';
    reason: string;
  }>;
  metadata: {
    createdAt: string;
    model: string;
    revisionNotes: string;
  };
}

/**
 * Get all transcript revisions for a session
 */
export async function getTranscriptRevisions(
  sessionId: string,
  authToken: string
): Promise<{ revisions: TranscriptRevision[]; selectedChecksum: string | null }> {
  const response = await fetch(
    `${API_BASE_URL}/sessions/${sessionId}/transcript-revisions`,
    {
      headers: {
        'Authorization': `Bearer ${authToken}`
      }
    }
  );

  if (!response.ok) {
    throw new Error('Failed to fetch transcript revisions');
  }

  const data = await response.json();
  return {
    revisions: data.revisions,
    selectedChecksum: data.selectedChecksum
  };
}

/**
 * Get a specific transcript revision (full Deepgram JSON)
 */
export async function getTranscriptRevision(
  sessionId: string,
  checksum: string,
  authToken: string
): Promise<{ deepgram: any; metadata: any }> {
  const response = await fetch(
    `${API_BASE_URL}/sessions/${sessionId}/transcript-revisions/${checksum}`,
    {
      headers: {
        'Authorization': `Bearer ${authToken}`
      }
    }
  );

  if (!response.ok) {
    throw new Error('Failed to fetch transcript revision');
  }

  const data = await response.json();
  return {
    deepgram: data.deepgram,
    metadata: data.metadata
  };
}

/**
 * Get the diffgram for a revision
 */
export async function getTranscriptDiffgram(
  sessionId: string,
  checksum: string,
  authToken: string
): Promise<{ diffgram: TranscriptDiffgram | null; revisionNotes: string | null }> {
  const response = await fetch(
    `${API_BASE_URL}/sessions/${sessionId}/transcript-revisions/${checksum}/diffgram`,
    {
      headers: {
        'Authorization': `Bearer ${authToken}`
      }
    }
  );

  if (!response.ok) {
    throw new Error('Failed to fetch diffgram');
  }

  const data = await response.json();
  return {
    diffgram: data.diffgram,
    revisionNotes: data.revisionNotes
  };
}

/**
 * Select a transcript revision for a session
 */
export async function selectTranscriptRevision(
  sessionId: string,
  checksum: string,
  authToken: string
): Promise<void> {
  const response = await fetch(
    `${API_BASE_URL}/sessions/${sessionId}/selected-transcript`,
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`
      },
      body: JSON.stringify({ checksum })
    }
  );

  if (!response.ok) {
    throw new Error('Failed to select transcript revision');
  }
}

/**
 * Delete a transcript revision
 * Returns the new selected checksum if the deleted revision was selected
 */
export async function deleteTranscriptRevision(
  sessionId: string,
  checksum: string,
  authToken: string
): Promise<{ newSelectedChecksum: string | null }> {
  const response = await fetch(
    `${API_BASE_URL}/sessions/${sessionId}/transcript-revisions/${checksum}`,
    {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${authToken}`
      }
    }
  );

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || 'Failed to delete transcript revision');
  }

  const data = await response.json();
  return { newSelectedChecksum: data.newSelectedChecksum };
}

// ============= PUBLIC SHARE ENDPOINTS =============

/**
 * Get transcript revisions for a public share (read-only)
 * Always returns latest revision as selected
 */
export async function getPublicTranscriptRevisions(
  shareToken: string
): Promise<{ revisions: TranscriptRevision[]; selectedChecksum: string | null }> {
  const response = await fetch(
    `${API_BASE_URL}/public/share/${shareToken}/transcript-revisions`
  );

  if (!response.ok) {
    throw new Error('Failed to fetch transcript revisions');
  }

  const data = await response.json();
  return {
    revisions: data.revisions,
    selectedChecksum: data.selectedChecksum
  };
}

/**
 * Get a specific transcript revision for a public share
 */
export async function getPublicTranscriptRevision(
  shareToken: string,
  checksum: string
): Promise<{ deepgram: any; metadata: any }> {
  const response = await fetch(
    `${API_BASE_URL}/public/share/${shareToken}/transcript-revisions/${checksum}`
  );

  if (!response.ok) {
    throw new Error('Failed to fetch transcript revision');
  }

  const data = await response.json();
  return {
    deepgram: data.deepgram,
    metadata: data.metadata
  };
}

/**
 * Get the diffgram for a revision in a public share
 */
export async function getPublicTranscriptDiffgram(
  shareToken: string,
  checksum: string
): Promise<{ diffgram: TranscriptDiffgram | null; revisionNotes: string | null }> {
  const response = await fetch(
    `${API_BASE_URL}/public/share/${shareToken}/transcript-revisions/${checksum}/diffgram`
  );

  if (!response.ok) {
    throw new Error('Failed to fetch diffgram');
  }

  const data = await response.json();
  return {
    diffgram: data.diffgram,
    revisionNotes: data.revisionNotes
  };
}
